/**
 * 동시판매(인터넷/인터넷+TV 동시 구매 시) 휴대폰 추가 할인금
 *
 * 서버 스펙 (POST /v1/quotation 등 시세표 제출·조회):
 *   discountWithInternet: [{ optionCode: 'INTERNET' | 'INT_TV', telecom: 'SK'|'KT'|'LG', ncDiscount, cdDiscount }]
 *
 * 화면은 내부 타입(BundleSaleItem)만 다루고, 서버 필드명·enum 값은 BUNDLE_SALE_API 한 곳에만 둔다.
 */

export type BundleSaleTelecom = 'SK' | 'KT' | 'LG'
export type BundleSaleOption = 'INTERNET' | 'INTERNET_TV'

export interface BundleSaleItem {
  telecom: BundleSaleTelecom
  option: BundleSaleOption
  /** 번호이동 추가 할인금 (만원, 소수점 한자리) — 미지원은 null */
  ncDiscount: number | string | null
  /** 기기변경 추가 할인금 (만원, 소수점 한자리) — 미지원은 null */
  cdDiscount: number | string | null
}

/** 서버 필드명·값 매핑 — 바뀌면 여기만 수정 */
export const BUNDLE_SALE_API = {
  /** 시세표 요청/응답 안에서 배열이 담기는 키 */
  formKey: 'discountWithInternet',
  field: {
    telecom: 'telecom',
    option: 'optionCode',
    ncDiscount: 'ncDiscount',
    cdDiscount: 'cdDiscount',
  },
  /** 내부 option → 서버 optionCode */
  optionValue: {
    INTERNET: 'INTERNET',
    INTERNET_TV: 'INT_TV',
  } satisfies Record<BundleSaleOption, string>,
} as const

export const BUNDLE_SALE_TELECOMS: BundleSaleTelecom[] = ['SK', 'KT', 'LG']

/** 동시판매 최소 유지기간(개월) — 화면 표시용 고정값 (M+N 개월, 서버 전송 없음) */
export const BUNDLE_SALE_MIN_PERIOD_MONTHS = 13

/** 행 라벨 — 인터넷 상품 기준 속도(500Mbps)를 라벨에 포함 */
export const BUNDLE_SALE_PRODUCTS: {
  option: BundleSaleOption
  label: string
}[] = [
  { option: 'INTERNET', label: '인터넷(500Mbps)만' },
  { option: 'INTERNET_TV', label: '인터넷(500Mbps)+TV' },
]

/** 통신사 3개 × 상품 2개 빈 폼 (서버도 같은 6개 조합을 항상 내려준다) */
export const initBundleSales = (): BundleSaleItem[] =>
  BUNDLE_SALE_TELECOMS.flatMap((telecom) =>
    BUNDLE_SALE_PRODUCTS.map(({ option }) => ({
      telecom,
      option,
      ncDiscount: null,
      cdDiscount: null,
    })),
  )

const toNumberOrNull = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null
  const n = Number(v)
  return Number.isNaN(n) ? null : n
}

const serverToOption = (value: unknown): BundleSaleOption | null => {
  const found = (
    Object.entries(BUNDLE_SALE_API.optionValue) as [BundleSaleOption, string][]
  ).find(([, serverValue]) => serverValue === value)
  return found ? found[0] : null
}

/** 내부 폼 → 서버 payload */
export const toBundleSalePayload = (
  items: BundleSaleItem[] | undefined,
): Record<string, unknown>[] => {
  const { field: F, optionValue } = BUNDLE_SALE_API
  return (items ?? initBundleSales()).map((item) => ({
    [F.option]: optionValue[item.option],
    [F.telecom]: item.telecom,
    [F.ncDiscount]: toNumberOrNull(item.ncDiscount),
    [F.cdDiscount]: toNumberOrNull(item.cdDiscount),
  }))
}

/**
 * 서버 응답 → 내부 폼.
 * 응답에 없거나 비어 있는 조합은 null 로 채워 항상 3통신사 × 2상품 구조를 유지한다.
 */
export const fromBundleSaleResponse = (raw: unknown): BundleSaleItem[] => {
  const { field: F } = BUNDLE_SALE_API
  const rows: Record<string, unknown>[] = Array.isArray(raw)
    ? raw.filter((r) => r && typeof r === 'object')
    : []

  return initBundleSales().map((empty) => {
    const found = rows.find(
      (r) =>
        r[F.telecom] === empty.telecom &&
        serverToOption(r[F.option]) === empty.option,
    )
    if (!found) return empty
    return {
      ...empty,
      ncDiscount: toNumberOrNull(found[F.ncDiscount]),
      cdDiscount: toNumberOrNull(found[F.cdDiscount]),
    }
  })
}

/**
 * 제출용 폼 변환 — 내부 bundleSales 를 서버 키(formKey)/서버 필드명으로 바꿔 끼운다.
 * postQuotation / postPartnerQuotation 호출 직전에 사용.
 */
export const withBundleSalePayload = <
  T extends { bundleSales?: BundleSaleItem[] },
>(
  form: T,
): Omit<T, 'bundleSales'> & Record<string, unknown> => {
  const { bundleSales, ...rest } = form
  return {
    ...rest,
    [BUNDLE_SALE_API.formKey]: toBundleSalePayload(bundleSales),
  }
}
